import * as Radix from '@radix-ui/react-dropdown-menu';
import { Icon } from '@toggle/design-system';
import clsx from 'clsx';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Option } from '~/components/filter/Option';
import {
  EVENT_ECONOMIC_COUNTRY_FILTER,
  useRemoteStorage,
} from '~/hooks/use-remote-storage';
import { useFinancialEventsCountries } from '~/react-query-hooks/events/useFinancialEventsCountries';
import { FilterSearch } from '~/views/screener/filter-search/FilterSearch';

import { CountryFilter } from '../utils';
import styles from './CountrySelector.module.scss';

export interface CountrySelectorProps {
  activeCountries: CountryFilter[];
  setActiveCountries: (value: React.SetStateAction<CountryFilter[]>) => void;
}

export const CountrySelector = ({
  activeCountries,
  setActiveCountries,
}: CountrySelectorProps) => {
  const { t } = useTranslation(['calendars']);
  const { data } = useFinancialEventsCountries();
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const { storeItems, deleteItem } = useRemoteStorage(state => ({
    storeItems: state.storeItems,
    deleteItem: state.deleteItem,
  }));

  const isActive = !!activeCountries.length;
  const showResetButton = !searchValue && isActive;
  const optionCount = activeCountries.length;
  const countryOptions =
    data
      ?.filter(d => {
        return searchValue
          ? true
          : !activeCountries
              .map(country => country.value.value)
              .includes(d.code);
      })
      .filter(d => {
        const lowerCaseSearchValue = searchValue.toLowerCase();
        return searchValue
          ? d.name.toLowerCase().includes(lowerCaseSearchValue) ||
              d.code.toLowerCase().includes(lowerCaseSearchValue)
          : true;
      }) ?? [];
  const showEmpty = !countryOptions.length && searchValue;

  const renderOptionsContent = () => {
    return (
      <>
        {!searchValue && !!activeCountries.length && (
          <>
            <Radix.Separator className={styles.separator} />
            <div className={styles.dropdownMenuTitle}>
              <p className={styles.dropdownMenuTitleText}>
                {t('calendar:countrySelector.selected')}
              </p>
            </div>
            {activeCountries?.map(c => (
              <Option
                key={c.key}
                isChecked
                option={c}
                handleClick={() => onSelectOption(c)}
                multiselect
              />
            ))}
          </>
        )}
        <div className={styles.dropdownMenuTitle}>
          <p className={styles.dropdownMenuTitleText}>
            {t('calendar:countrySelector.countries')}
          </p>
        </div>

        {countryOptions.map(({ name, code }) => {
          const option = {
            key: code,
            name: name,
            value: { value: code },
          };

          return (
            <Option
              key={code}
              isChecked={!!activeCountries.find(c => c.value.value === code)}
              option={option}
              handleClick={() => onSelectOption(option)}
              multiselect
            />
          );
        })}
      </>
    );
  };

  const onClearSearchInput = () => {
    setSearchValue('');
  };

  const handleOutsideClick = () => {
    onClearSearchInput();
    setOpen(false);
  };

  const onSelectOption = (countryCode: CountryFilter) => {
    const isNewCountryCode = !activeCountries.find(
      a => a.value.value === countryCode.value.value
    );
    const newFilterOptions = isNewCountryCode
      ? [...activeCountries, countryCode]
      : activeCountries.filter(a => a.value.value !== countryCode.value.value);

    setActiveCountries(newFilterOptions);
    storeItems({ [EVENT_ECONOMIC_COUNTRY_FILTER]: newFilterOptions });
  };

  return (
    <div data-testid="events-country-selector">
      <Radix.Root open={open} onOpenChange={setOpen}>
        <Radix.Trigger
          className={clsx(styles.trigger, {
            [styles.triggerActive]: isActive,
          })}
          onClick={() => setOpen(true)}
        >
          <span className={styles.triggerLabel}>
            {t('calendar:countrySelector.country')}
          </span>
          {isActive && (
            <span className={styles.selectedValue}>
              {optionCount > 1 ? (
                <span className={styles.countBadge}>{optionCount}</span>
              ) : (
                activeCountries[0].name
              )}
            </span>
          )}
          <Icon
            iconName="ChevronLightDown"
            size={14}
            className={styles.triggerArrow}
          />
        </Radix.Trigger>
        <Radix.Portal>
          <Radix.Content
            className={styles.dropdownMenuContent}
            align="start"
            sideOffset={5}
            collisionPadding={5}
            onInteractOutside={handleOutsideClick}
          >
            <>
              <>
                <FilterSearch
                  value={searchValue}
                  searchPlaceholder={t('calendar:countrySelector.search')}
                  onChange={e => setSearchValue(e.target.value)}
                  onClear={onClearSearchInput}
                />
                {showResetButton && (
                  <div>
                    <Radix.Separator className={styles.separator} />
                  </div>
                )}
              </>

              {showResetButton && (
                <Radix.Item
                  className={styles.resetItem}
                  onSelect={() => {
                    setActiveCountries([]);
                    deleteItem(EVENT_ECONOMIC_COUNTRY_FILTER);
                  }}
                >
                  <p className={styles.resetButton} data-testid="reset-label">
                    <span className={styles.resetIconWrapper}>
                      <Icon
                        iconName="ArrowRotateRight"
                        size={14}
                        className={styles.resetIcon}
                      />
                    </span>
                    <span className={styles.resetButtonLabel}>
                      {t('calendar:countrySelector.reset')}
                    </span>
                  </p>
                </Radix.Item>
              )}
              <div className={styles.scrollableList}>
                {showEmpty ? (
                  <p className={styles.noResults}>
                    {t('calendar:countrySelector.noResults')}
                  </p>
                ) : (
                  renderOptionsContent()
                )}
              </div>
            </>
          </Radix.Content>
        </Radix.Portal>
      </Radix.Root>
    </div>
  );
};
